import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { CITIES } from "@/data/cities";
import { breadcrumbListJsonLd, medicalWebPageJsonLd } from "@/data/schema";

export default function Cities() {
  return (
    <div data-testid="cities-index-page" className="pt-28 md:pt-36 pb-28">
      <SEO
        title="Regions served — Vascular & Endovascular IR at Max Mohali · Dr Tegbir Vascular"
        description="Twelve regional catchments served by Dr Tegbir Sidhu's IR practice — from tri-city Chandigarh–Mohali–Panchkula out to Punjab, Haryana, Himachal, J&K and Delhi NCR."
        keywords={["vascular treatment chandigarh", "interventional radiology punjab", "vascular specialist mohali", "IR treatment delhi ncr"]}
      />
      <JsonLd data={[
        medicalWebPageJsonLd({ name: "Regions served", description: "Cities and regions from which Dr Tegbir Sidhu accepts referrals.", url: "/cities" }),
        breadcrumbListJsonLd([{ to: "/", label: "Home" }, { to: "/cities", label: "Regions" }]),
      ]}/>
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { label: "Regions" }]} />
          <div className="mt-8 kbd">— Where patients come from</div>
          <h1 className="mt-6 font-serif text-6xl md:text-[8vw] leading-[0.96] tracking-tightest max-w-5xl">
            <MaskedReveal text={"Twelve regions."}/>
            <MaskedReveal text={"One practice"} delay={0.1}/>
            <MaskedReveal text={"in Mohali."} delay={0.2} className="italic font-normal"/>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ink-muted leading-relaxed">
            From the tri-city core out to the Punjab industrial belt, the Himachal hills, the J&K corridor, and the NCR — the practice's regional catchment is deliberate, and travel logistics are handled by the team, not the patient.
          </p>
        </FadeUp>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 hairline">
          {CITIES.map((c, i) => (
            <FadeUp key={c.slug} delay={i * 0.03} className="bg-bone">
              <Link to={`/cities/${c.slug}`} data-testid={`city-card-${c.slug}`} className="group block p-6 md:p-8 h-full hover:bg-white transition-colors">
                <div className="flex items-start justify-between">
                  <div className="kbd">{c.region}</div>
                  <ArrowUpRight size={16} className="text-ink-muted group-hover:text-crimson group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"/>
                </div>
                <h3 className="mt-4 font-serif text-3xl tracking-tightest group-hover:text-crimson transition-colors">{c.name}</h3>
                <p className="mt-2 text-xs text-ink-muted">{c.tag}</p>
                <div className="mt-4 kbd">{c.distance}</div>
              </Link>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-24">
          <div className="hairline-t pt-12 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-8">
              <h3 className="font-serif text-4xl md:text-5xl tracking-tightest leading-tight">Don't see your city?</h3>
              <p className="mt-4 text-ink-muted max-w-xl">The practice regularly receives patients from beyond these twelve regions — from the rest of India, the Gulf, and the Anglosphere. WhatsApp with your city and we'll respond with logistics.</p>
            </div>
            <div className="col-span-12 md:col-span-4 md:pl-8 self-end flex flex-wrap gap-3">
              <Link to="/contact" className="btn-pill-inv">Enquire <ArrowUpRight size={14}/></Link>
              <Link to="/nri" className="btn-pill">For NRI</Link>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
